import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { Control, Controller, FieldErrors, FieldValues } from "react-hook-form";
import { useEffect, useState } from "react";
import { FormHelperText, RadioGroup } from "@mui/material";
import { Field, FlightSearchResult } from "@/core/types/types";
import FlightSelectItem from "./FlightSelectItem";
import { RadioButtonGroupItem } from "@flightright/reusable-components";
import { getFormattedTime } from "@/core/utils";

const NOT_FOUND: string = "not-found";

interface FlightsSelectProps {
  flights: FlightSearchResult[];
  selectedFlight?: FlightSearchResult | null;
  onFlightSelect: (selectedFlight: FlightSearchResult | null) => void;
  control: Control<FieldValues, any>;
  field: Field;
  errors: FieldErrors<FieldValues>;
  required?: boolean;
}

export interface IFormatedFlightData {
  formattedDepartureDate?: string;
  formattedDepartureTime: string;
  formattedArrivalTime: string;
  formattedFlightNumber: string;
}

const FlightsSelect = ({
  flights,
  onFlightSelect,
  control,
  field,
  errors,
  required = true,
}: FlightsSelectProps) => {
  const error: string | undefined = errors[field.key]
    ? `${errors[field.key]?.message}`
    : undefined;
  const errorMessage: string = t(`validate.${field.key}.required.message`);
  const flightNotFoundText = t("flightPicker.notFound.message");
  const [uniqueKey, setUniqueKey] = useState(generateRandomKey());

  useEffect(() => {
    setUniqueKey(generateRandomKey());
  }, [flights]);

  return (
    <Controller
      name={field.key}
      control={control}
      rules={{
        // "required" validation for case of null values
        validate: (v) => {
          const isValid = v !== undefined || errorMessage;
          return required ? isValid : true;
        },
      }}
      render={() => {
        return (
          <>
            <RadioGroup
              id={field.key}
              color="secondary"
              key={uniqueKey}
              name="flight-list"
              sx={{ gap: 1 }}
            >
              {flights.map((flight: FlightSearchResult) => {
                const formattedFlightData: IFormatedFlightData = {
                  formattedDepartureTime: getFormattedTime(
                    flight.scheduledDeparture
                  ),
                  formattedArrivalTime: getFormattedTime(
                    flight.scheduledArrival
                  ),
                  formattedFlightNumber: getFormattedFlightNumber(flight),
                };
                const { flightNumber } = flight;

                return (
                  <FlightSelectItem
                    key={flightNumber}
                    flight={flight}
                    flightNumber={flightNumber}
                    onSelectFlight={onFlightSelect}
                    formattedFlightData={formattedFlightData}
                  />
                );
              })}
              {!!flights.length && (
                <RadioButtonGroupItem
                  key={NOT_FOUND}
                  value={NOT_FOUND}
                  labelStyles={{ minHeight: 48 }}
                  onChange={() => {
                    onFlightSelect(null);
                  }}
                  label={flightNotFoundText}
                />
              )}
            </RadioGroup>
            <FormHelperText error={!!error}>{error}</FormHelperText>
          </>
        );
      }}
    />
  );
};

function getFormattedFlightNumber(flight: FlightSearchResult) {
  if (flight.flightNumber.includes(flight.operatingAirlineCode)) {
    return flight.flightNumber;
  }
  return flight.operatingAirlineCode.concat(flight.flightNumber);
}

function generateRandomKey(length = 16) {
  const values = new Uint8Array(length);
  crypto.getRandomValues(values);
  return Array.from(values, (byte) => byte.toString(16)).join("");
}

export { FlightsSelect };
